'use client';

import { type RefObject, useLayoutEffect } from 'react';
import { computeMasonryLayout } from '@/libs/utils/masonry';
import {
  PC_MASONRY_GAP,
  PC_MEDIA_QUERY,
  PC_THREE_COLUMN_MEDIA_QUERY,
} from '@/libs/config/responsive';

/** 열 하나의 최소 폭(px). 769px 화면의 2열(348.5px)보다 작게 잡아 기본 규칙을 바꾸지 않는다. */
const MIN_COLUMN_WIDTH = 300;

/** masonry 배치 대상 카드에 붙이는 data 속성. */
export const MASONRY_ITEM_ATTRIBUTE = 'data-masonry-item';

interface UseMasonryOptions {
  /** 배치가 끝나 absolute 배치로 바뀌었을 때 컨테이너에 붙일 클래스. */
  readyClassName: string;
}

const getItems = (container: HTMLElement) =>
  Array.from(container.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && child.hasAttribute(MASONRY_ITEM_ATTRIBUTE),
  );

const clearItemStyles = (item: HTMLElement) => {
  item.style.removeProperty('width');
  item.style.removeProperty('transform');
};

// 폭이 바뀌어 재배치할 때 보고 있던 카드가 화면에서 같은 자리에 남도록 기준 카드를 고른다.
// 초점이 목록 안에 있으면 그 카드, 아니면 화면 위쪽에서 처음 보이는 카드다.
const findAnchor = (items: HTMLElement[]) => {
  const focused = document.activeElement;
  const focusedItem = focused ? items.find(item => item.contains(focused)) : undefined;
  if (focusedItem) return focusedItem;

  return items.find((item) => {
    const rect = item.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  });
};

/**
 * PC(769px 이상)에서 컨테이너의 직계 카드들을 핀터레스트형 masonry로 배치한다.
 *
 * - 1280px 이상 3열, 769–1279px 2열, 간격 24px(lone-web-pc pc-v2-handoff).
 * - 카드는 DOM 순서(= API 순서)를 그대로 두고 transform으로만 옮기므로 스크린리더·Tab 순서가 바뀌지 않는다.
 * - 새 카드는 가장 짧은 열에 붙고 기존 카드는 움직이지 않는다. 폭이 바뀔 때만 전체를 다시 배치하며,
 *   이때 초점 카드(없으면 보이던 첫 카드)의 화면 위치를 유지한다.
 * - 모바일(768px 이하)에서는 아무 스타일도 남기지 않아 기존 1열 목록이 그대로다.
 *
 * 첫 배치 전(서버 렌더·하이드레이션 직후)에는 컨테이너 CSS의 그리드가 같은 열 폭으로 카드를 보여 준다.
 */
export const useMasonry = (containerRef: RefObject<HTMLElement | null>, { readyClassName }: UseMasonryOptions) => {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const pcQuery = window.matchMedia(PC_MEDIA_QUERY);
    const threeColumnQuery = window.matchMedia(PC_THREE_COLUMN_MEDIA_QUERY);
    let frame: number | undefined;
    let lastWidth = -1;
    let lastColumns = -1;

    const reset = () => {
      container.classList.remove(readyClassName);
      container.style.removeProperty('height');
      getItems(container).forEach(clearItemStyles);
      lastWidth = -1;
      lastColumns = -1;
    };

    const layout = () => {
      frame = undefined;

      if (!pcQuery.matches) {
        reset();
        return;
      }

      const items = getItems(container);
      const width = container.clientWidth;
      if (width <= 0) return;
      // 장소 상세처럼 옆에 레일이 있는 좁은 목록에서는 카드가 지나치게 좁아지지 않도록 열 수를 줄인다.
      const maxColumnsByWidth = Math.max(1, Math.floor((width + PC_MASONRY_GAP) / (MIN_COLUMN_WIDTH + PC_MASONRY_GAP)));
      const columns = Math.min(threeColumnQuery.matches ? 3 : 2, maxColumnsByWidth);

      const isResize = lastWidth !== -1 && (lastWidth !== width || lastColumns !== columns);
      const anchor = isResize ? findAnchor(items) : undefined;
      const anchorTop = anchor?.getBoundingClientRect().top;

      const columnWidth = (width - PC_MASONRY_GAP * (columns - 1)) / columns;
      items.forEach((item) => {
        item.style.width = `${columnWidth}px`;
      });

      // 폭을 먼저 모두 맞춘 뒤 한 번에 높이를 읽어 레이아웃 계산을 한 번으로 줄인다.
      const heights = items.map(item => item.offsetHeight);
      const { positions, height } = computeMasonryLayout(heights, columns, columnWidth, PC_MASONRY_GAP);

      items.forEach((item, index) => {
        const { x, y } = positions[index];
        item.style.transform = `translate(${x}px, ${y}px)`;
      });
      container.style.height = `${height}px`;
      container.classList.add(readyClassName);

      lastWidth = width;
      lastColumns = columns;

      if (anchor && anchorTop !== undefined) {
        const delta = anchor.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      }
    };

    const schedule = () => {
      if (frame === undefined) frame = window.requestAnimationFrame(layout);
    };

    // 카드 높이 변화(글꼴 로드, 좋아요 수 자릿수 등)와 컨테이너 폭 변화를 모두 다시 배치로 이어 준다.
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(container);
    getItems(container).forEach(item => resizeObserver.observe(item));

    // 무한 스크롤로 붙은 카드도 관찰 대상에 넣는다.
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.hasAttribute(MASONRY_ITEM_ATTRIBUTE)) {
            resizeObserver.observe(node);
          }
        });
      }
      schedule();
    });
    mutationObserver.observe(container, { childList: true });

    pcQuery.addEventListener('change', schedule);
    threeColumnQuery.addEventListener('change', schedule);

    layout();

    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      pcQuery.removeEventListener('change', schedule);
      threeColumnQuery.removeEventListener('change', schedule);
      reset();
    };
  }, [containerRef, readyClassName]);
};
